// components/use-case-details/use-case-section-navigator.tsx
"use client";

import { useEffect, useRef,useState } from 'react';

import { cn } from '@/lib/utils';

export interface Section {
  id: string;
  title: string;
  subsections?: Array<{
    id: string;
    title: string;
  }>;
}

interface UseCaseSectionNavigatorProps {
  sections: Section[];
}

export function UseCaseSectionNavigator({ sections }: UseCaseSectionNavigatorProps) {
  const [activeSection, setActiveSection] = useState<string>('');
  const [activeSubsection, setActiveSubsection] = useState<string>('');
  const [showNav, setShowNav] = useState(false);
  const observer = useRef<IntersectionObserver | null>(null);
  const heroObserver = useRef<IntersectionObserver | null>(null);
  
  // Set up intersection observer to track when we leave the hero section
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1
    };
    
    heroObserver.current = new IntersectionObserver((entries) => {
      // Hero element is intersecting (visible) - don't show nav
      if (entries.length > 0 && entries[0].isIntersecting) {
        setShowNav(false);
      } else {
        // We've scrolled past the hero
        setShowNav(true);
      }
    }, observerOptions);
    
    // We need to observe the hero section and stats banner
    const heroElement = document.querySelector('.hero-section');
    const statsBannerElement = document.querySelector('.stats-banner');
    
    if (heroElement) {
      heroObserver.current.observe(heroElement);
    }
    
    if (statsBannerElement) {
      heroObserver.current.observe(statsBannerElement);
    }
    
    return () => {
      if (heroObserver.current) {
        heroObserver.current.disconnect();
      }
    };
  }, []);
  
  // Set up intersection observer to track which section is in view
  useEffect(() => {
    const observerOptions = {
      rootMargin: '-80px 0px -60% 0px', // Adjusted to better detect sections
      threshold: [0.01, 0.05, 0.1] // Lower initial threshold for better detection
    };
    
    observer.current = new IntersectionObserver((entries) => {
      // Sort by highest intersection ratio first
      const sortedEntries = [...entries].sort((a, b) => 
        b.intersectionRatio - a.intersectionRatio
      );
      
      for (const entry of sortedEntries) {
        if (entry.isIntersecting) {
          const sectionId = entry.target.id;
          const sectionType = entry.target.getAttribute('data-section-type');
          
          // Enhanced logging for debugging
          console.log(`Section in view: ${sectionId}`, {
            ratio: entry.intersectionRatio,
            type: sectionType
          });
          
          // Special handling for workflow sections
          if (sectionId.startsWith('workflow-') || sectionType === 'workflow') {
            // This is a workflow subsection
            setActiveSection('workflows');
            setActiveSubsection(sectionId);
            
            // Ensure this section is forcibly activated in the DOM
            const workflowSection = document.getElementById('workflows');
            if (workflowSection) {
              workflowSection.classList.add('force-active');
            }
            break; // Exit after finding the most visible section
          }
          
          // Check if this is a section or subsection
          const isSection = sections.some(section => section.id === sectionId);
          
          if (isSection) {
            setActiveSection(sectionId);
            
            // If we're entering a new section, reset active subsection
            setActiveSubsection('');
            break; // Exit after finding the most visible section
          } else {
            // Must be a subsection - find which section it belongs to
            for (const section of sections) {
              if (section.subsections?.some(sub => sub.id === sectionId)) {
                setActiveSection(section.id);
                setActiveSubsection(sectionId);
                break;
              }
            }
            break; // Exit after finding the most visible section
          }
        }
      }
    }, observerOptions);
    
    // Observe all sections and subsections with special handling for workflows
    sections.forEach(section => {
      const sectionElement = document.getElementById(section.id);
      if (sectionElement) observer.current?.observe(sectionElement);
      
      // For workflows, ensure we're also looking at all workflow elements in the DOM
      if (section.id === 'workflows') {
        const workflowElements = document.querySelectorAll('[data-section-type="workflow"]');
        workflowElements.forEach(el => observer.current?.observe(el));
      }
      
      if (section.subsections) {
        section.subsections.forEach(subsection => {
          const subsectionElement = document.getElementById(subsection.id);
          if (subsectionElement) observer.current?.observe(subsectionElement);
        });
      }
    });
    
    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [sections]);
  
  // Add an additional effect to manually check for active workflow sections
  useEffect(() => {
    // Check if URL hash points to a workflow section
    const hash = window.location.hash;
    if (hash && hash.startsWith('#workflow-')) {
      const workflowId = hash.substring(1); // Remove the # character
      setActiveSection('workflows');
      setActiveSubsection(workflowId);
      
      // Ensure this section is forcibly activated in the DOM
      const workflowSection = document.getElementById('workflows');
      if (workflowSection) {
        workflowSection.classList.add('force-active');
      }
    }
    
    // Also check if we're viewing any workflow section on initial load
    const workflowElements = document.querySelectorAll('[data-section-type="workflow"]');
    if (workflowElements.length > 0) {
      const isAnyWorkflowVisible = Array.from(workflowElements).some(el => {
        const rect = el.getBoundingClientRect();
        // Check if element is in viewport
        return (
          rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
          rect.right <= (window.innerWidth || document.documentElement.clientWidth)
        );
      });
      
      if (isAnyWorkflowVisible) {
        setActiveSection('workflows');
      }
    }
  }, []);
  
  const scrollToSection = (sectionId: string) => {
    // Special handling for workflow sections - always show the workflows parent section first
    if (sectionId.startsWith('workflow-')) {
      setActiveSection('workflows');
      setActiveSubsection(sectionId);
      
      // Try to find the anchor element first for better scrolling
      const anchorElement = document.getElementById(`workflow-anchor-${sectionId.split('-')[1]}`);
      if (anchorElement) {
        const yOffset = -100; 
        const y = anchorElement.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
        return;
      }
    }
    
    const element = document.getElementById(sectionId);
    if (element) {
      // Scroll to element with offset for the sticky header
      const yOffset = -120; 
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };
  
  if (!showNav) return null;
  
  return (
    <div className="navigation-section z-50">
      {/* Desktop navigation - use the right column */}
      <div className="hidden lg:block right-auto lg:left-[calc(50%+520px)] fixed top-1/4">
        <div className="w-56 bg-gray-900/80 border border-gray-800 rounded-lg shadow-lg p-4 backdrop-blur-sm transition-opacity duration-300">
          <h3 className="text-sm font-bold text-gray-300 mb-3">On This Page</h3>
          <nav>
            <ul className="space-y-3">
              {sections.map(section => (
                <li key={section.id}>
                  <button
                    onClick={() => scrollToSection(section.id)}
                    className={cn(
                      "text-left text-sm w-full hover:text-yellow-400 transition-colors",
                      activeSection === section.id ? "text-yellow-400 font-medium" : "text-gray-400"
                    )}
                  >
                    {section.title}
                  </button>
                  
                  {/* Show workflow subsections or active section's subsections */}
                  {section.subsections && section.subsections.length > 0 && (
                    (section.id === 'workflows' && (activeSection === 'workflows' || activeSubsection?.startsWith('workflow-'))) || 
                    (activeSection === section.id)
                  ) && (
                    <ul className="pl-3 mt-2 space-y-1 border-l border-gray-800">
                      {section.subsections.map(subsection => (
                        <li key={subsection.id}>
                          <button
                            onClick={() => scrollToSection(subsection.id)}
                            className={cn(
                              "text-left text-xs w-full hover:text-yellow-400 transition-colors",
                              activeSubsection === subsection.id ? "text-yellow-400" : "text-gray-500"
                            )}
                          >
                            {subsection.title}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>
      
      {/* Mobile navigation - fixed to bottom of viewport */}
      <div className="block lg:hidden fixed bottom-4 right-4 left-4">
        <div className="bg-gray-900/95 border border-gray-800 rounded-lg shadow-lg p-4 backdrop-blur-sm max-w-xs mx-auto">
          <h3 className="text-sm font-bold text-gray-300 mb-3">Navigate to</h3>
          <nav>
            <select 
              className="w-full bg-gray-800 text-gray-300 border border-gray-700 rounded-md p-2 text-sm"
              value={activeSection}
              onChange={(e) => scrollToSection(e.target.value)}
            >
              {sections.map(section => (
                <option key={section.id} value={section.id}>
                  {section.title}
                </option>
              ))}
            </select>
            
            {activeSection && (() => {
              const activeSubsections = sections.find(s => s.id === activeSection)?.subsections || [];
              return activeSubsections.length > 0 ? (
                <select 
                  className="w-full bg-gray-800 text-gray-300 border border-gray-700 rounded-md p-2 text-sm mt-2"
                  value={activeSubsection}
                  onChange={(e) => scrollToSection(e.target.value)}
                >
                  <option value="">-- Section details --</option>
                  {activeSubsections.map(subsection => (
                    <option key={subsection.id} value={subsection.id}>
                      {subsection.title}
                    </option>
                  ))}
                </select>
              ) : null;
            })()}
          </nav>
        </div>
      </div>
    </div>
  );
} 